<script setup>
import AppLayout from '@/layouts/AppLayout.vue'
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { getOrdersFn } from '@/services/api/restApiCalls'
import MobileAppPromotion from '@/components/MobileAppPromotion.vue'
import { useDateTime } from '@/composables/useDateTime'
import {
  ShoppingBagIcon,
  EyeIcon,
  BanknotesIcon,
  CreditCardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/vue/20/solid'

const { formatDateTime } = useDateTime()

const restoStore = useRestoGLobalStore()
const router = useRouter()
const ordersData = ref([])
const currentPage = ref(1)
const perPage = ref(10)
const total = ref(0)
const lastPage = ref(0)

const handleGetOrder = async () => {
  const orders_data = await getOrdersFn(currentPage.value, restoStore?.RESTO_ID, router)
  if (orders_data?.status === 401) return
  ordersData.value = orders_data?.orders ?? []
  currentPage.value = parseInt(orders_data?.current_page, 10) || 1
  perPage.value = orders_data?.records_per_page ?? 10
  total.value = orders_data?.total_records ?? 0
  lastPage.value = orders_data?.last_page ?? 0
}
const handlePageClick = async (page) => {
  if (page < 1 || page > lastPage.value) return
  currentPage.value = page
  handleGetOrder()
}

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    case 'AUTHORIZED':
    case 'CANCELLED':
    case 'FAILURE':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }
}

const handleViewOrder = (order) => {
  if (order.order_status === 'AUTHORIZED') {
    router.push({
      name: 'order_status',
      params: { order_id: `${order.order_id}` }
    })
  } else {
    router.push({
      name: 'view_order',
      params: { order_id: `${order.order_id}` }
    })
  }
}

onMounted(() => {
  handleGetOrder()
})
</script>
<template>
  <AppLayout>
    <div class="container mx-auto dark:bg-background-dark pt-8">
      <div class="sm:flex sm:items-center mb-2">
        <div class="sm:flex-auto">
          <h1
            class="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 text-center md:mb-6 mb-4"
          >
            My Orders
          </h1>
        </div>
      </div>
      <!-- Mobile Card View -->
      <div class="mx-2 md:hidden space-y-4 mb-4">
        <div
          v-for="order in ordersData"
          :key="order.order_id"
          class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm"
        >
          <!-- Header: Order ID and Status -->
          <div class="flex justify-between items-start mb-4">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Order ID</p>
              <p class="text-base font-semibold text-indigo-600 dark:text-indigo-400">
                #{{ order.order_id }}
              </p>
            </div>
            <span
              class="px-3 py-1 rounded-full text-xs font-medium"
              :class="getStatusColor(order.order_status)"
            >
              {{ order.order_status }}
            </span>
          </div>

          <!-- Details Grid -->
          <div class="grid grid-cols-2 gap-4 mb-4">
            <!-- Order Type -->
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">TYPE</p>
              <div class="flex items-center">
                <span
                  class="flex items-center px-2 py-1 text-white rounded-full text-xs w-fit"
                  :class="[order?.order_type == 'delivery' ? 'bg-green-600 dark:bg-green-700' : 'bg-violet-600 dark:bg-violet-700']"
                >
                  <span v-if="order.order_type == 'collection'" class="mr-1">
                    <ShoppingBagIcon class="h-3 w-3 text-white" aria-hidden="true" />
                  </span>
                  <span v-if="order.order_type == 'delivery'" class="mr-1">
                    <svg
                      class="h-3 w-3 text-white fill-current"
                      focusable="false"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M20 11c-.18 0-.36.03-.53.05L17.41 9H20V6l-3.72 1.86L13.41 5H9v2h3.59l2 2H11l-4 2-2-2H0v2h4c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4l2 2h3l3.49-6.1 1.01 1.01c-.91.73-1.5 1.84-1.5 3.09 0 2.21 1.79 4 4 4s4-1.79 4-4-1.79-4-4-4zM4 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm16 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
                      ></path>
                    </svg>
                  </span>
                  <span class="capitalize">{{ order.order_type }}</span>
                </span>
              </div>
            </div>

            <!-- Payment Type -->
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">PAYMENT</p>
              <div class="flex items-center">
                <span
                  class="flex items-center px-2 py-1 text-white rounded-full text-xs w-fit capitalize"
                  :class="[order?.payment_type == 'cash' ? 'bg-yellow-600 dark:bg-yellow-700' : 'bg-purple-600 dark:bg-purple-700']"
                >
                  <span v-if="order.payment_type == 'cash'" class="mr-1">
                    <BanknotesIcon class="h-3 w-3 text-white" aria-hidden="true" />
                  </span>
                  <span v-if="order.payment_type == 'card'" class="mr-1">
                    <CreditCardIcon class="h-3 w-3 text-white" aria-hidden="true" />
                  </span>
                  {{ order.payment_type }}
                </span>
              </div>
            </div>

            <!-- Amount -->
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">AMOUNT</p>
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                £{{ order.amount }}
              </p>
            </div>

            <!-- Date -->
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">DATE & TIME</p>
              <div class="flex items-center"> 
                <CalendarIcon class="h-4 w-4 mr-1 text-gray-400 dark:text-gray-500" />
                <p class="text-sm text-gray-900 dark:text-gray-300">
                  {{ formatDateTime(order.creation_datetime) }}
                </p>
              </div>
            </div>
          </div>

          <!-- View Button -->
          <button
            @click="handleViewOrder(order)"
            class="w-full mt-4 bg-amber-700 dark:bg-amber-800 hover:bg-amber-800 dark:hover:bg-amber-900 text-white font-medium py-2.5 px-4 rounded-lg flex items-center justify-center transition-colors"
          >
            <EyeIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            View Order Details
          </button>
        </div>

        <!-- Empty State -->
        <div
          v-if="ordersData.length === 0"
          class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center"
        >
          <p class="text-gray-500 dark:text-gray-400">No orders found</p>
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="mx-2 hidden md:block">
        <div class="flow-root">
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <table
              class="min-w-full divide-y divide-gray-300 dark:divide-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm"
            >
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 sm:pl-2 pl-1 md:pr-3 sm:pr-2 pr-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Order Id
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100 hidden md:block"
                  >
                    Date & Time
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Order Type
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100 hidden md:block"
                  >
                    Payment Type
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-left sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 md:px-3 sm:px-2 px-1 text-right sm:text-base text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    View
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                <tr
                  v-for="order in ordersData"
                  :key="order.order_id"
                  @click="
                    order.order_status === 'AUTHORIZED'
                      ? router.push({
                          name: 'order_status',
                          params: { order_id: `${order.order_id}` }
                        })
                      : router.push({
                          name: 'view_order',
                          params: { order_id: `${order.order_id}` }
                        })
                  "
                  class="dark:hover:bg-gray-700 hover:bg-gray-50"
                >
                  <td
                    class="whitespace-nowrap py-3 sm:pl-2 pl-1 md:pr-3 sm:pr-2 pr-1 sm:text-base text-sm md:font-semibold text-indigo-500 dark:text-indigo-400 cursor-pointer"
                    @click.stop="
                      order.order_status === 'AUTHORIZED'
                        ? router.push({
                            name: 'order_status',
                            params: { order_id: `${order.order_id}` }
                          })
                        : router.push({
                            name: 'view_order',
                            params: { order_id: `${order.order_id}` }
                          })
                    "
                  >
                    {{ order.order_id }}
                  </td>
                  <td
                    class="whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 text-sm text-gray-900 dark:text-gray-300 hidden md:block"
                  >
                    <div class="flex items-center">
                      <CalendarIcon class="h-4 w-4 mr-1 text-gray-400 dark:text-gray-500" />
                      {{ formatDateTime(order.creation_datetime) }}
                    </div>
                  </td>
                  <td
                    class="whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 text-sm text-gray-900 dark:text-gray-300 capitalize sm:font-medium font-normal"
                  >
                    <span
                      class="flex items-center sm:px-4 px-2 py-1 text-white rounded-full text-sm w-fit"
                      :class="[order?.order_type == 'delivery' ? 'bg-green-600 dark:bg-green-700' : 'bg-violet-600 dark:bg-violet-700']"
                    >
                      <span v-if="order.order_type == 'collection'" class="sm:mr-1.5 mr-1">
                        <ShoppingBagIcon class="h-4 w-4 text-white mx-auto" aria-hidden="true" />
                      </span>
                      <span v-if="order.order_type == 'delivery'" class="sm:mr-1.5 mr-1">
                        <svg
                          class="h-4 w-4 text-white fill-current"
                          focusable="false"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M20 11c-.18 0-.36.03-.53.05L17.41 9H20V6l-3.72 1.86L13.41 5H9v2h3.59l2 2H11l-4 2-2-2H0v2h4c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4l2 2h3l3.49-6.1 1.01 1.01c-.91.73-1.5 1.84-1.5 3.09 0 2.21 1.79 4 4 4s4-1.79 4-4-1.79-4-4-4zM4 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm16 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
                          ></path>
                        </svg>
                      </span>
                      {{ order.order_type }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 text-sm text-gray-900 dark:text-gray-300">
                    £{{ order.amount }}
                  </td>
                  <td
                    class="whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 text-sm text-gray-900 dark:text-gray-300 capitalize font-medium hidden md:block"
                  >
                    <span
                      class="flex items-center sm:px-4 px-2 py-1 text-white rounded-full text-sm w-fit"
                      :class="[order?.payment_type == 'cash' ? 'bg-yellow-600 dark:bg-yellow-700' : 'bg-purple-600 dark:bg-purple-700']"
                    >
                      <span v-if="order.payment_type == 'cash'" class="sm:mr-1.5 mr-1">
                        <BanknotesIcon class="h-4 w-4 text-white mx-auto" aria-hidden="true" />
                      </span>
                      <span v-if="order.payment_type == 'card'" class="sm:mr-1.5 mr-1">
                        <CreditCardIcon class="h-4 w-4 text-white mx-auto" aria-hidden="true" />
                      </span>
                      {{ order.payment_type }}
                    </span>
                  </td>
                  <td
                    class="whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 sm:text-sm text-xs text-gray-900 dark:text-gray-300"
                    :class="{
                      'text-red-600 dark:text-red-400':
                        order.order_status == 'AUTHORIZED' ||
                        order.order_status == 'CANCELLED' ||
                        order.order_status == 'FAILURE',
                      'text-green-600 dark:text-green-400': order.order_status == 'SUCCESS'
                    }"
                  >
                    {{ order.order_status }}
                  </td>
                  <td
                    class="relative whitespace-nowrap py-3 md:px-3 sm:px-2 px-1 text-right text-sm font-medium"
                  >
                    <button
                      class="w-full text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center justify-end"
                      @click.stop="
                        order.order_status === 'AUTHORIZED'
                          ? $router.push({
                              name: 'order_status',
                              params: { order_id: `${order.order_id}` }
                            })
                          : $router.push({
                              name: 'view_order',
                              params: { order_id: `${order.order_id}` }
                            })
                      "
                    >
                      <EyeIcon class="h-5 w-5 text-indigo-500 dark:text-indigo-400 mr-1" aria-hidden="true" />
                      <span class="hidden sm:inline-block">Show</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      <div
        class="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 mx-2"
      >
        <div class="flex flex-1 items-center justify-end">
          <div>
            <nav
              class="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                class="relative inline-flex items-center rounded-l-md sm:px-2 px-1 sm:py-2 py-1 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500"
                @click="handlePageClick(currentPage - 1)"
                :disabled="currentPage == 1"
              >
                <span class="sr-only">Previous</span>
                <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
              </button>
              <!-- Current: "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600", Default: "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0" -->
              <template v-for="(page, index) in lastPage" :key="index">
                <button
                  class="relative inline-flex items-center sm:px-4 px-3 sm:py-2 py-1 text-sm font-semibold text-gray-900 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-700"
                  :class="[
                    currentPage == index + 1
                      ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                      : ''
                  ]"
                  @click="handlePageClick(index + 1)"
                >
                  {{ index + 1 }}
                </button>
              </template>

              <button
                class="relative inline-flex items-center rounded-r-md sm:px-2 px-1 sm:py-2 py-1 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500"
                @click="handlePageClick(currentPage + 1)"
                :disabled="currentPage == lastPage"
              >
                <span class="sr-only">Next</span>
                <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Mobile App Promotion Component -->
      <div class="mx-2 mt-6 px-4 py-4 bg-white dark:bg-gray-800 rounded-lg shadow transition-colors duration-200">
        <MobileAppPromotion />
      </div>
    </div>
  </AppLayout>
</template>
